import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Card, 
  CardContent, 
  CardHeader, 
  Tabs, 
  Tab, 
  Button, 
  CircularProgress, 
  IconButton, 
  Chip, 
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer, 
  Legend, 
  PieChart, 
  Pie, 
  Cell,
  CartesianGrid,
  Bar,
  BarChart
} from 'recharts';
import { 
  FaCalendarAlt, 
  FaArrowLeft, 
  FaArrowRight, 
  FaChartLine, 
  FaChartBar, 
  FaChartPie, 
} from 'react-icons/fa';
import { format, addDays, addWeeks, isToday, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth, addMonths, eachWeekOfInterval, getWeek, isSameMonth, } from 'date-fns';
import { fr } from 'date-fns/locale';
import { salesStatsService, DailySalesStats, WeeklySalesStats, ItemSalesStat, MonthlySalesStats } from '../../services/salesStatsService';
import { categoriesService } from '../../services/categoriesService';
import { formatCurrency } from '../../utils/helpers';
import { useFirebase } from '../Auth/FirebaseProvider';

// Composant stylisé pour les cartes de statistiques
const StatsCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[8],
  },
}));

// Composant stylisé pour la visualisation des Top Items
const TopItemCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

// Couleurs pour les graphiques
const CHART_COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
  '#82CA9D', '#FF6B6B', '#6A7FDB', '#CCA43B', '#5D5C61'
];

// Interface pour les props des tabs
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Composant TabPanel
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`stats-tabpanel-${index}`}
      aria-labelledby={`stats-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Fonction pour créer des props d'accessibilité pour les tabs
function a11yProps(index: number) {
  return {
    id: `stats-tab-${index}`,
    'aria-controls': `stats-tabpanel-${index}`,
  };
}

// Composant principal de la page de statistiques
const SalesStatsPage = () => {
  const { foodTruckId } = useFirebase();
  
  // State pour la gestion des onglets
  const [tabValue, setTabValue] = useState(0);
  
  // State pour les dates
  const [currentDate, setCurrentDate] = useState(new Date());
  const [weekStartDate, setWeekStartDate] = useState<Date>(startOfWeek(currentDate, { locale: fr }));
  const [weekEndDate, setWeekEndDate] = useState<Date>(endOfWeek(currentDate, { locale: fr }));
  const [monthStartDate, setMonthStartDate] = useState<Date>(startOfMonth(currentDate));
  const [monthEndDate, setMonthEndDate] = useState<Date>(endOfMonth(currentDate));
  
  // State pour les données statistiques
  const [dailyStats, setDailyStats] = useState<DailySalesStats | null>(null);
  const [weeklyStats, setWeeklyStats] = useState<WeeklySalesStats | null>(null);
  const [monthlyStats, setMonthlyStats] = useState<MonthlySalesStats | null>(null);

  
  // State pour le chargement
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State pour les catégories
  const [categories, setCategories] = useState<any[]>([]);
  
  // Effet pour charger les catégories
  useEffect(() => {
    if (!foodTruckId) return;
    
    const loadCategories = async () => {
      try {
        const catService = categoriesService.getInstance(foodTruckId);
        const categoriesData = await catService.getCategories();
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Impossible de charger les catégories');
      }
    };
    
    loadCategories();
  }, [foodTruckId]);

  // Fonction pour charger les statistiques journalières
  const loadDailyStats = async () => {
    if (!foodTruckId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const statsService = salesStatsService.getInstance(foodTruckId);
      const stats = await statsService.getDailyStats(currentDate);
      setDailyStats(stats);
    } catch (err) {
      console.error('Error fetching daily stats:', err);
      setError('Impossible de charger les statistiques journalières');
    } finally {
      setLoading(false);
    }
  };
    
  // Fonction pour charger les statistiques hebdomadaires
  const loadWeeklyStats = async () => {
    if (!foodTruckId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const statsService = salesStatsService.getInstance(foodTruckId);
      
      // Mettre à jour les dates de début et fin de semaine
      setWeekStartDate(startOfWeek(currentDate, { locale: fr }));
      setWeekEndDate(endOfWeek(currentDate, { locale: fr }));
      
      const stats = await statsService.getWeeklyStats(currentDate);
      setWeeklyStats(stats);
    } catch (err) {
      console.error('Error fetching weekly stats:', err);
      setError('Impossible de charger les statistiques hebdomadaires');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour charger les statistiques mensuelles
  const loadMonthlyStats = async () => {
    if (!foodTruckId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log("loadMonthlyStats");
      const statsService = salesStatsService.getInstance(foodTruckId);
      
      // Mettre à jour les dates de début et fin du mois
      setMonthStartDate(startOfMonth(currentDate));
      setMonthEndDate(endOfMonth(currentDate));
      
      const stats = await statsService.getMonthlyStats(currentDate);
      setMonthlyStats(stats);
    } catch (err) {
      console.error('Error fetching monthly stats:', err);
      setError('Impossible de charger les statistiques mensuelles');
    } finally {
      setLoading(false);
    }
  };

    // Effet pour charger les données selon l'onglet actif
    useEffect(() => {
      if (!foodTruckId || categories.length === 0) return;
      
      switch (tabValue) {
        case 0:
          loadDailyStats();
          break;
        case 1:
          loadWeeklyStats();
          break;
        case 2:
          loadMonthlyStats();
          break;
      }
    }, [foodTruckId, currentDate, categories, tabValue]);

  // Handler pour le changement d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Navigation dans les dates
  const navigateToNextDay = () => {
    setCurrentDate(prevDate => addDays(prevDate, 1));
  };

  const navigateToPreviousDay = () => {
    setCurrentDate(prevDate => addDays(prevDate, -1));
  };

  const navigateToNextWeek = () => {
    setCurrentDate(prevDate => addWeeks(prevDate, 1));
  };

  const navigateToPreviousWeek = () => {
    setCurrentDate(prevDate => addWeeks(prevDate, -1));
  };

  const navigateToNextMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, 1));
  };
  
  const navigateToPreviousMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, -1));
  };
  
  const formatMonthHeader = (date: Date) => {
    return format(date, "MMMM yyyy", { locale: fr });
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };  

  // Fonction pour formater les dates
  const formatDateHeader = (date: Date) => {
    return format(date, "EEEE d MMMM yyyy", { locale: fr });
  };

  const formatWeekHeader = (start: Date, end: Date) => {
    return `Semaine du ${format(start, "d MMMM", { locale: fr })} au ${format(end, "d MMMM yyyy", { locale: fr })}`;
  };
  
  // Préparation des données pour les graphiques
  const getTop5Items = (stats: ItemSalesStat[] | undefined, byRevenue = false): ItemSalesStat[] => {
    if (!stats || stats.length === 0) return [];
    
    const sortedItems = [...stats].sort((a, b) => 
      byRevenue ? b.revenue - a.revenue : b.quantity - a.quantity
    );
    
    return sortedItems.slice(0, 5);
  };
  
  const prepareDailyRevenueData = (stats: WeeklySalesStats | null) => {
    if (!stats) return [];
    
    const days = eachDayOfInterval({
      start: weekStartDate,
      end: weekEndDate
    });
    
    return days.map(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      return {
        name: format(day, 'EEE', { locale: fr }),
        revenue: stats.dailyRevenue[dateKey] || 0,
        fullDate: format(day, 'dd/MM')
      };
    });
  };

  const prepareWeeklyRevenueData = (stats: MonthlySalesStats | null) => {
    if (!stats) return [];
    
    const weeks = eachWeekOfInterval(
      {
        start: monthStartDate,
        end: monthEndDate
      },
      { locale: fr }
    );
    
    return weeks.map(weekStart => {
      const weekEnd = endOfWeek(weekStart, { locale: fr });
      const weekKey = `${format(weekStart, 'yyyy-MM-dd')}_${format(weekEnd, 'yyyy-MM-dd')}`;
      const weekNumber = getWeek(weekStart, { locale: fr });
      
      return {
        name: `S${weekNumber}`,
        revenue: stats.weeklyRevenue[weekKey] || 0,
        fullDate: `${format(weekStart, 'dd/MM')} - ${format(weekEnd, 'dd/MM')}`
      };
    });
  };
  
  // Rendu du tableau de bord journalier
  const renderDailyDashboard = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography color="error" align="center">{error}</Typography>
        </Box>
      );
    }
    
    if (!dailyStats) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography align="center">Aucune donnée disponible pour cette journée.</Typography>
        </Box>
      );
    }
    
    const top5Items = getTop5Items(dailyStats.popularItems);
    
    return (
      <Grid container spacing={3}>
        {/* Carte des commandes */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Commandes" 
              subheader="Total du jour"
              avatar={<FaChartLine size={24} color="#0088FE" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {dailyStats.totalOrders}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {dailyStats.totalItems} articles vendus
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Carte du chiffre d'affaires */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Chiffre d'Affaires" 
              subheader="Total du jour"
              avatar={<FaChartBar size={24} color="#00C49F" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(dailyStats.totalRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {formatCurrency(dailyStats.totalRevenue / Math.max(1, dailyStats.totalOrders))} par commande en moyenne
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Carte du panier moyen */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Articles par Commande" 
              subheader="Moyenne du jour"
              avatar={<FaChartPie size={24} color="#FFBB28" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {(dailyStats.totalItems / Math.max(1, dailyStats.totalOrders)).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                articles par commande
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Top 5 des produits vendus */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
            Top 5 des produits les plus vendus
          </Typography>
          <Grid container spacing={2}>
            {top5Items.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.id}>
                <TopItemCard>
                  <CardHeader
                    title={`#${index + 1}`}
                    titleTypographyProps={{ variant: 'subtitle1', align: 'center' }}
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ pt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                          mb: 1
                        }}
                      >
                        {item.quantity}
                      </Box>
                      <Typography variant="body1" align="center" sx={{ fontWeight: 'bold' }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {formatCurrency(item.revenue)}
                      </Typography>
                      {item.categoryName && (
                        <Chip 
                          label={item.categoryName} 
                          size="small" 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                  </CardContent>
                </TopItemCard>
              </Grid>
            ))}
          </Grid>
        </Grid>
        
        {/* Graphique des produits vendus */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 3 }}>
            <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={top5Items}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="quantity"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {top5Items.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name, props) => [value, 'Quantité']}
                      labelFormatter={(label) => top5Items.find(item => item.name === label)?.name || ''}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };
  
  // Rendu du tableau de bord hebdomadaire
  const renderWeeklyDashboard = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography color="error" align="center">{error}</Typography>
        </Box>
      );
    }
    
    if (!weeklyStats) {
      return (
        <Box sx={{ py: 4 }}>
          <Typography align="center">Aucune donnée disponible pour cette semaine.</Typography>
        </Box>
      );
    }
    
    const top5WeeklyItems = getTop5Items(weeklyStats.popularItems);
    const weeklyRevenueData = prepareDailyRevenueData(weeklyStats);
    
    return (
      <Grid container spacing={3}>
        {/* Carte des commandes */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Commandes" 
              subheader="Total de la semaine"
              avatar={<FaChartLine size={24} color="#0088FE" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {weeklyStats.totalOrders}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {weeklyStats.totalItems} articles vendus
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Carte du chiffre d'affaires */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Chiffre d'Affaires" 
              subheader="Total de la semaine"
              avatar={<FaChartBar size={24} color="#00C49F" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(weeklyStats.totalRevenue)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {formatCurrency(weeklyStats.totalRevenue / Math.max(1, weeklyStats.totalOrders))} par commande en moyenne
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Carte du panier moyen */}
        <Grid item xs={12} sm={6} md={4}>
          <StatsCard>
            <CardHeader 
              title="Articles par Commande" 
              subheader="Moyenne de la semaine"
              avatar={<FaChartPie size={24} color="#FFBB28" />} 
            />
            <CardContent>
              <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
                {(weeklyStats.totalItems / Math.max(1, weeklyStats.totalOrders)).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                articles par commande
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
        
        {/* Graphique d'évolution du chiffre d'affaires */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Évolution du chiffre d'affaires</Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={weeklyRevenueData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip 
                    formatter={(value) => [formatCurrency(value as number), 'Chiffre d\'affaires']}
                    labelFormatter={(value) => `${weeklyRevenueData.find(data => data.name === value)?.fullDate || ''}`}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    name="Chiffre d'affaires" 
                    stroke="#0088FE" 
                    activeDot={{ r: 8 }} 
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
        
        {/* Top 5 des produits vendus */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
            Top 5 des produits les plus vendus (semaine)
          </Typography>
          <Grid container spacing={2}>
            {top5WeeklyItems.map((item, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.id}>
                <TopItemCard>
                  <CardHeader
                    title={`#${index + 1}`}
                    titleTypographyProps={{ variant: 'subtitle1', align: 'center' }}
                    sx={{ pb: 0 }}
                  />
                  <CardContent sx={{ pt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1.5rem',
                          mb: 1
                        }}
                      >
                        {item.quantity}
                      </Box>
                      <Typography variant="body1" align="center" sx={{ fontWeight: 'bold' }}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" align="center">
                        {formatCurrency(item.revenue)}
                      </Typography>
                      {item.categoryName && (
                        <Chip 
                          label={item.categoryName} 
                          size="small" 
                          sx={{ mt: 1 }} 
                        />
                      )}
                    </Box>
                  </CardContent>
                </TopItemCard>
              </Grid>
            ))}
          </Grid>
        </Grid>
        
        {/* Graphique des produits vendus (semaine) */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mt: 3 }}>
            <Box sx={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={top5WeeklyItems}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="quantity"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {top5WeeklyItems.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value) => [value, 'Quantité']}
                      labelFormatter={(label) => top5WeeklyItems.find(item => item.name === label)?.name || ''}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  // Rendu du tableau de bord mensuel
const renderMonthlyDashboard = () => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="error" align="center">{error}</Typography>
      </Box>
    );
  }
  
  if (!monthlyStats) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography align="center">Aucune donnée disponible pour ce mois.</Typography>
      </Box>
    );
  }
  
  const top5MonthlyItems = getTop5Items(monthlyStats.popularItems);
  const monthlyRevenueData = prepareWeeklyRevenueData(monthlyStats);
  
  return (
    <Grid container spacing={3}>
      {/* Carte des commandes */}
      <Grid item xs={12} sm={6} md={4}>
        <StatsCard>
          <CardHeader 
            title="Commandes" 
            subheader="Total du mois"
            avatar={<FaChartLine size={24} color="#0088FE" />} 
          />
          <CardContent>
            <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
              {monthlyStats.totalOrders}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {monthlyStats.totalItems} articles vendus
            </Typography>
          </CardContent>
        </StatsCard>
      </Grid>
      
      {/* Carte du chiffre d'affaires */}
      <Grid item xs={12} sm={6} md={4}>
        <StatsCard>
          <CardHeader 
            title="Chiffre d'Affaires" 
            subheader="Total du mois"
            avatar={<FaChartBar size={24} color="#00C49F" />} 
          />
          <CardContent>
            <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(monthlyStats.totalRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {formatCurrency(monthlyStats.totalRevenue / Math.max(1, monthlyStats.totalOrders))} par commande en moyenne
            </Typography>
          </CardContent>
        </StatsCard>
      </Grid>
      
      {/* Carte du panier moyen */}
      <Grid item xs={12} sm={6} md={4}>
        <StatsCard>
          <CardHeader 
            title="Articles par Commande" 
            subheader="Moyenne du mois"
            avatar={<FaChartPie size={24} color="#FFBB28" />} 
          />
          <CardContent>
            <Typography variant="h3" align="center" sx={{ fontWeight: 'bold' }}>
              {(monthlyStats.totalItems / Math.max(1, monthlyStats.totalOrders)).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              articles par commande
            </Typography>
          </CardContent>
        </StatsCard>
      </Grid>
      
      {/* Graphique d'évolution du chiffre d'affaires par semaine */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Évolution du chiffre d'affaires par semaine</Typography>
          <Box sx={{ height: 300, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyRevenueData}>
                <XAxis dataKey="name" />
                <YAxis />
                <CartesianGrid strokeDasharray="3 3" />
                <RechartsTooltip 
                  formatter={(value) => [formatCurrency(value as number), 'Chiffre d\'affaires']}
                  labelFormatter={(value) => `${monthlyRevenueData.find(data => data.name === value)?.fullDate || ''}`}
                />
                <Legend />
                <Bar 
                  dataKey="revenue" 
                  name="Chiffre d'affaires" 
                  fill="#0088FE" 
                />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Grid>
      
      {/* Top 5 des produits vendus */}
      <Grid item xs={12}>
        <Typography variant="h5" sx={{ mb: 2, mt: 3, fontWeight: 'bold' }}>
          Top 5 des produits les plus vendus (mois)
        </Typography>
        <Grid container spacing={2}>
          {top5MonthlyItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={item.id}>
              <TopItemCard>
                <CardHeader
                  title={`#${index + 1}`}
                  titleTypographyProps={{ variant: 'subtitle1', align: 'center' }}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ pt: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '1.5rem',
                        mb: 1
                      }}
                    >
                      {item.quantity}
                    </Box>
                    <Typography variant="body1" align="center" sx={{ fontWeight: 'bold' }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" align="center">
                      {formatCurrency(item.revenue)}
                    </Typography>
                    {item.categoryName && (
                      <Chip 
                        label={item.categoryName} 
                        size="small" 
                        sx={{ mt: 1 }} 
                      />
                    )}
                  </Box>
                </CardContent>
              </TopItemCard>
            </Grid>
          ))}
        </Grid>
      </Grid>
      
      {/* Graphique des produits vendus (mois) */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 3 }}>
          <Box sx={{ height: 300, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={top5MonthlyItems}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="quantity"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {top5MonthlyItems.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip 
                  formatter={(value) => [value, 'Quantité']}
                  labelFormatter={(label) => top5MonthlyItems.find(item => item.name === label)?.name || ''}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Paper>
      </Grid>

      {/* Analyse par catégorie */}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Répartition des ventes par catégorie</Typography>
          
          {Object.entries(monthlyStats.itemsByCategory).length > 0 ? (
            <Box sx={{ height: 400, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={Object.entries(monthlyStats.itemsByCategory).map(([categoryId, items]) => {
                    const categoryName = items[0]?.categoryName || categoryId;
                    const totalRevenue = items.reduce((sum, item) => sum + item.revenue, 0);
                    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
                    
                    return {
                      name: categoryName,
                      revenue: totalRevenue,
                      quantity: totalQuantity,
                      categoryId
                    };
                  }).sort((a, b) => b.quantity - a.quantity)}
                  margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" />
                  <RechartsTooltip
                    formatter={(value, name) => [
                       value,
                       'Quantité'
                    ]}
                  />
                  <Legend />
                  <Bar dataKey="quantity" name="Quantité" fill="#0088FE" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Typography align="center">Aucune donnée disponible par catégorie.</Typography>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};
  
  return (
    <Box sx={{ p: 0 }}>
      {/* Header de la page */}
      <Box sx={{ 
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        p: 3,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaChartLine size={20} color="white" />
              </Box>
              Statistiques de ventes
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mt: 0.5
              }}
            >
              Analysez vos performances commerciales
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Contenu principal */}
      <Box sx={{ px: 3 }}>
      {/* Onglets */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="stats tabs"
          sx={{ mb: 2 }}
        >
          <Tab label="Statistiques Journalières" {...a11yProps(0)} />
          <Tab label="Statistiques Hebdomadaires" {...a11yProps(1)} />
          <Tab label="Statistiques Mensuels" {...a11yProps(2)} />
        </Tabs>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={navigateToPreviousDay}>
              <FaArrowLeft />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 2 }}>
              {formatDateHeader(currentDate)}
              {isToday(currentDate) && (
                <Chip label="Aujourd'hui" size="small" color="primary" sx={{ ml: 1 }} />
              )}
            </Typography>
            <IconButton onClick={navigateToNextDay} disabled={isToday(currentDate)}>
              <FaArrowRight />
            </IconButton>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FaCalendarAlt />}
            onClick={navigateToToday}
            disabled={isToday(currentDate)}
          >
            Aujourd'hui
          </Button>
        </Box>
        
        {renderDailyDashboard()}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 3
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={navigateToPreviousWeek}>
              <FaArrowLeft />
            </IconButton>
            <Typography variant="h6" sx={{ mx: 2 }}>
              {formatWeekHeader(weekStartDate, weekEndDate)}
            </Typography>
            <IconButton onClick={navigateToNextWeek} disabled={isToday(weekEndDate)}>
              <FaArrowRight />
            </IconButton>
          </Box>
          <Button
            variant="outlined"
            startIcon={<FaCalendarAlt />}
            onClick={navigateToToday}
            disabled={isToday(currentDate)}
          >
            Cette semaine
          </Button>
        </Box>
        
        {renderWeeklyDashboard()}
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center',
    mb: 3
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <IconButton onClick={navigateToPreviousMonth}>
        <FaArrowLeft />
      </IconButton>
      <Typography variant="h6" sx={{ mx: 2 }}>
        {formatMonthHeader(currentDate)}
        {isSameMonth(currentDate, new Date()) && (
          <Chip label="Mois en cours" size="small" color="primary" sx={{ ml: 1 }} />
        )}
      </Typography>
      <IconButton onClick={navigateToNextMonth} disabled={isSameMonth(currentDate, new Date())}>
        <FaArrowRight />
      </IconButton>
    </Box>
    <Button
      variant="outlined"
      startIcon={<FaCalendarAlt />}
      onClick={navigateToToday}
      disabled={isSameMonth(currentDate, new Date())}
    >
      Mois en cours
    </Button>
  </Box>
  
  {renderMonthlyDashboard()}
</TabPanel>
      </Box>
    </Box>
  );
};

export default SalesStatsPage;